import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NotificationToast } from 'app/layout/common/notification-toast/notification-toast.types';

/**
 * Transient toast stack for incoming notifications.
 *
 * Producer side: `MessagesComponent` calls `show()` for every notification the
 * socket delivers that was not already in the bell list.
 * Consumer side: `NotificationToastComponent` renders `toasts$` and reports a
 * click back through `activate()`; the producer listens on `activated$` and
 * does the routing plus mark-as-read with its existing logic.
 */
@Injectable({ providedIn: 'root' })
export class NotificationToastService {
    /** A burst of notifications must never wall off the screen. */
    private static readonly MAX_VISIBLE = 3;

    private readonly _toasts: BehaviorSubject<NotificationToast[]> = new BehaviorSubject<NotificationToast[]>([]);
    private readonly _activated: Subject<NotificationToast> = new Subject<NotificationToast>();

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /** Visible stack, newest first. */
    get toasts$(): Observable<NotificationToast[]> {
        return this._toasts.asObservable();
    }

    /** Emits when the user clicks a toast body or its View action. */
    get activated$(): Observable<NotificationToast> {
        return this._activated.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Push a toast onto the stack. Re-showing the same id replaces the existing
     * entry rather than stacking a duplicate.
     */
    show(toast: NotificationToast): void {
        const rest = this._toasts.value.filter(item => item.id !== toast.id);
        this._toasts.next([toast, ...rest].slice(0, NotificationToastService.MAX_VISIBLE));
    }

    /** Remove one toast (auto-dismiss, close button, or after activation). */
    dismiss(id: string): void {
        const next = this._toasts.value.filter(item => item.id !== id);
        if (next.length !== this._toasts.value.length) {
            this._toasts.next(next);
        }
    }

    /** Drop everything on screen (sign-out). */
    clear(): void {
        if (this._toasts.value.length) {
            this._toasts.next([]);
        }
    }

    /**
     * Report that the user acted on a toast. Deliberately does not remove it -
     * the component runs its exit animation and calls `dismiss()` afterwards.
     */
    activate(toast: NotificationToast): void {
        this._activated.next(toast);
    }
}
